// src/components/resources/calculators/MerCalculator.tsx

'use client'

import { useState, useMemo } from 'react'
import { motion, AnimatePresence } from 'motion/react'
import { cn } from '~/lib/utils'
import RiMoneyDollarCircleLine from '~/icons/remix/generated/RiMoneyDollarCircleLine'
import RiPercentLine from '~/icons/remix/generated/RiPercentLine'
import RiUserAddLine from '~/icons/remix/generated/RiUserAddLine'
import RiLineChartLine from '~/icons/remix/generated/RiLineChartLine'
import RiInformationLine from '~/icons/remix/generated/RiInformationLine'
import RiLightbulbLine from '~/icons/remix/generated/RiLightbulbLine'
import RiAlertLine from '~/icons/remix/generated/RiAlertLine'
import { FAQ } from '~/components/shared/FAQ'
import { EmbeddedEmailCapture } from '~/components/forms/variants/embedded/EmbeddedEmailCapture'
import type { EmbeddedEmailCaptureProps } from '~/components/forms/variants/embedded/types'
import Link from 'next/link'
import { NumberInput } from '~/components/shared/NumberInput'
import { formatInputValue } from '~/lib/utils/format/input'
import { createEngagementTimers, sendResourceEvent } from '~/lib/gtag'
import { debounce } from '~/lib/utils/events'
import { calculateMetrics, validateInputs, type CalculatorInputs, type CalculatorResults } from './MerCalculator.logic'

interface EmailTemplateConfig {
  templateAlias: string
  to: string
  from: string
  templateModel: {
    base: {
      name: string
      logo_url: string
      product_name: string
      product_url: string
      support_email: string
      sender_name: string
      company_name: string
      company_address: string
      email: string
    }
    custom: {
      calculator_type: string
      calculator_results: {
        metrics: CalculatorResults
        inputs: CalculatorInputs
      } | null
    }
  }
}

// Define the tooltip info structure
interface TooltipInfo {
  title: string
  description: string
  tips: string[]
}

// Define the tooltips object with proper typing
const TOOLTIPS: Record<string, TooltipInfo> = {
  totalRevenue: {
    title: "Total Revenue",
    description: "The sum of all revenue generated during the period, including both attributed and organic revenue.",
    tips: [
      "Include all revenue sources for an accurate MER calculation",
      "For eCommerce, this is typically gross merchandise value (GMV)"
    ]
  },
  totalMarketingSpend: {
    title: "Total Marketing Spend",
    description: "The sum of all marketing expenses during the period, including ad spend, agency fees, creative costs, etc.",
    tips: [
      "Include all marketing costs for the most accurate efficiency metrics",
      "Consider including marketing team salaries for a fully loaded view"
    ]
  },
  attributedRevenue: {
    title: "Attributed Revenue",
    description: "Revenue directly attributed to your paid marketing channels through your attribution system.",
    tips: [
      "This varies based on your attribution model (first-click, last-click, multi-touch)",
      "Typically lower than total revenue as it excludes organic/direct revenue"
    ]
  },
  newCustomerRevenue: {
    title: "New Customer Revenue",
    description: "Revenue generated from first-time customers only.",
    tips: [
      "Critical for evaluating acquisition efficiency",
      "Helps isolate growth metrics from retention metrics"
    ]
  },
  mer: {
    title: "Marketing Efficiency Ratio (MER)",
    description: "Measures overall marketing effectiveness by comparing total revenue to total marketing costs.",
    tips: [
      "Higher MER indicates more efficient marketing spend",
      "Industry benchmarks vary, but 3-5x is often considered good"
    ]
  },
  amer: {
    title: "Attributed Marketing Efficiency Ratio (aMER)",
    description: "Measures efficiency of paid marketing by comparing attributed revenue to total marketing spend.",
    tips: [
      "Typically lower than MER as it only counts attributed revenue",
      "Useful for evaluating direct response marketing performance"
    ]
  },
  nmer: {
    title: "New Marketing Efficiency Ratio (nMER)",
    description: "Measures acquisition efficiency by comparing new customer revenue to marketing spend.",
    tips: [
      "Critical for evaluating customer acquisition strategies",
      "Usually lower than overall MER due to excluding repeat purchases"
    ]
  }
}

const FAQ_ITEMS = [
  {
    question: "What is Marketing Efficiency Ratio (MER)?",
    answer: "Marketing Efficiency Ratio (MER) measures the overall effectiveness of marketing spend by comparing total revenue to total marketing costs. It provides a holistic view of marketing performance across all channels and customer types. The formula is: MER = Total Revenue / Total Marketing Spend. For example, if your business generated $100,000 in revenue with $20,000 in marketing spend, your MER would be 5.0. Unlike channel-specific metrics, MER captures the full business impact of your marketing investments."
  },
  {
    question: "What's the difference between MER, aMER, and nMER?",
    answer: "These metrics measure marketing efficiency from different angles: MER (Marketing Efficiency Ratio) uses total revenue divided by marketing spend to show overall efficiency. aMER (Attributed MER) uses only revenue attributed to paid channels, helping evaluate direct marketing impact. nMER (New MER) focuses on new customer revenue, measuring acquisition efficiency specifically. Using these metrics together provides a comprehensive view of both overall marketing performance and specific growth drivers."
  },
  {
    question: "What is a good MER?",
    answer: "A good MER varies by industry, business model, and growth stage. Generally, a MER of 3.0 or higher is considered good for established businesses, while growth-focused companies might accept lower MERs (1.5-2.5) to prioritize expansion. Subscription businesses often start with lower MERs but improve over time as recurring revenue accumulates. E-commerce typically targets 3-4x, while SaaS companies may accept 1.5-2.5x during growth phases and aim for 3-5x at maturity."
  },
  {
    question: "How is MER different from ROAS?",
    answer: "While similar, MER and ROAS differ in scope: ROAS (Return on Ad Spend) specifically measures revenue generated from advertising spend only. MER (Marketing Efficiency Ratio) takes a broader view by including all marketing costs (creative, agency fees, team salaries) and all revenue (including organic). MER provides a more comprehensive view of marketing efficiency. ROAS is typically higher than MER since it excludes non-advertising costs that MER accounts for, making MER a more conservative and complete efficiency metric."
  },
  {
    question: "Why is my aMER lower than my MER?",
    answer: "aMER (Attributed MER) is typically lower than overall MER because it only counts revenue directly attributed to paid marketing channels, excluding organic and direct revenue. This difference actually helps quantify the 'halo effect' of your marketing - the gap between attributed revenue and total revenue often represents brand building and organic growth influenced by your marketing efforts. A large gap may indicate strong brand equity or attribution challenges that need addressing."
  },
  {
    question: "How can I improve my marketing efficiency ratios?",
    answer: "To improve your marketing efficiency ratios: 1) Optimize campaign targeting to reduce wasted spend, 2) Improve creative performance to increase conversion rates, 3) Enhance your product/market fit to boost customer value, 4) Refine your pricing strategy, 5) Invest in retention marketing to increase customer lifetime value, and 6) Improve attribution to better understand which channels drive results. Additionally, consider implementing incrementality testing to measure true marketing impact beyond last-click attribution."
  },
  {
    question: "Should I include organic revenue in my MER calculation?",
    answer: "Yes, including organic revenue in your MER calculation is recommended as it provides a complete picture of marketing effectiveness. Marketing efforts often influence organic channels through brand awareness and consideration, even when not directly attributed. However, it's valuable to calculate both MER (with all revenue) and aMER (attributed revenue only) to understand the full spectrum of your marketing impact and identify potential attribution gaps."
  }
]

export function MerCalculator(): JSX.Element {
  const [inputs, setInputs] = useState<CalculatorInputs>({
    totalRevenue: 100000,
    totalMarketingSpend: 25000,
    attributedRevenue: 50000,
    newCustomerRevenue: 30000,
    includeAdvancedMetrics: true
  })

  const [results, setResults] = useState<CalculatorResults | null>(null)
  // Snapshot of the inputs the current `results` were computed from, so the
  // CRM/email payload never pairs live inputs with stale metrics after an edit.
  const [resultInputs, setResultInputs] = useState<CalculatorInputs | null>(null)
  const [showEmailCapture, setShowEmailCapture] = useState(false)
  const [email, setEmail] = useState('')
  const [activeTooltip, setActiveTooltip] = useState<string | null>(null)
  const [validationMessages, setValidationMessages] = useState<Array<{type: 'warning'; message: string}>>([])
  const [getTotalMs, getDeltaMs] = useMemo(() => createEngagementTimers(), [])

  // Debounced, lean interact tracking
  const _debouncedInteract = useMemo(() => debounce((...args: unknown[]) => {
    const interactionType = (args[0] as string) ?? 'input_change'
    sendResourceEvent('interact', {
      resourceType: 'calculator',
      resourceSlug: 'mer-calculator',
      engagementMs: getDeltaMs(),
      params: { interaction_type: interactionType }
    })
  }, 400), [getDeltaMs])

  const trackInteract = (interactionType: string): void => {
    _debouncedInteract(interactionType)
  }

  const handleCalculate = (): void => {
    // Check for validation warnings and update state with them
    const validationWarnings = validateInputs(inputs)
    setValidationMessages(validationWarnings)

    // Calculate results even if there are warnings
    const newResults = calculateMetrics(inputs)
    setResults(newResults)
    setResultInputs(inputs)
    setShowEmailCapture(true)
    sendResourceEvent('interact', {
      resourceType: 'calculator',
      resourceSlug: 'mer-calculator',
      engagementMs: getDeltaMs(),
      params: { interaction_type: 'calculate' }
    })
    sendResourceEvent('complete', {
      resourceType: 'calculator',
      resourceSlug: 'mer-calculator',
      engagementMs: getTotalMs(),
      params: { mer: newResults.mer, amer: newResults.amer, nmer: newResults.nmer }
    })
  }

  const emailTemplate = useMemo(() => {
    // Base email template configuration
    const baseTemplate: EmailTemplateConfig = {
      templateAlias: 'welcome',
      to: '',
      from: process.env.NEXT_PUBLIC_EMAIL_FROM ?? 'info@adsights.ai',
      templateModel: {
        base: {
          name: email ? email.split('@')[0]?.trim() ?? '' : '',
          logo_url: 'https://www.adsights.ai/logo-square.png',
          product_name: 'AdSights',
          product_url: 'https://www.adsights.ai',
          support_email: 'support@adsights.ai',
          sender_name: 'The AdSights Team',
          company_name: 'AdSights Inc.',
          company_address: '254 Chapman Rd, Ste 208 #28056, Newark, Delaware 19702',
          email: email ?? ''
        },
        custom: {
          calculator_type: 'Marketing Efficiency Ratio',
          calculator_results: results ? {
            metrics: results,
            inputs: inputs
          } : null
        }
      }
    }

    return baseTemplate
  }, [email, results, inputs])

  const emailCaptureConfig: EmbeddedEmailCaptureProps = {
    preset: 'indigo',
    content: {
      title: {
        text: "Get Your Marketing Efficiency Analysis"
      },
      description: {
        text: "We'll send you a detailed breakdown of your metrics and personalized recommendations."
      },
      button: {
        text: "Get Analysis",
        loadingText: "Preparing Analysis...",
        icon: null,
        className: "flex w-auto items-center justify-center rounded-md bg-indigo-600 px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
      },
      input: {
        label: "Work Email",
        placeholder: "Enter your work email"
      },
      messages: {
        success: "Analysis sent!",
        error: "Please enter a valid email address."
      }
    },
    template: emailTemplate,
    analytics: {
      enabled: true,
      category: 'calculators',
      action: 'email_capture',
      label: 'mer_calculator_results',
      elementId: 'mer-calculator-email-form',
      pageLocation: 'MER Calculator'
    },
    crm: {
      tags: ['calculator_results', 'mer'],
      source: 'mer_calculator',
      // Read from the `resultInputs` snapshot, never live `inputs`, so an edit
      // after Calculate can't store a mismatched input/metric pair.
      customFields: {
        calculator_type: 'MER',
        calculator_total_revenue: resultInputs?.totalRevenue,
        calculator_marketing_spend: resultInputs?.totalMarketingSpend,
        calculator_mer: results?.mer
      }
    },
    // Using onSubmit ensures the email state is only updated when the form is submitted,
    // not on every keystroke. This is more efficient and prevents unnecessary re-renders.
    onSubmit: (submittedEmail) => setEmail(submittedEmail)
  }

  const renderTooltip = (key: string): JSX.Element | null => {
    const info = TOOLTIPS[key]
    if (!info) return null

    return (
      <div className="relative ml-2 inline-block">
        <button
          type="button"
          className="text-gray-400 hover:text-gray-500"
          aria-label={`Learn more about ${info.title}`}
          onMouseEnter={() => setActiveTooltip(key)}
          onMouseLeave={() => setActiveTooltip(null)}
        >
          <RiInformationLine className="h-5 w-5" />
        </button>
        {activeTooltip === key && (
          <div className="absolute -right-4 top-8 z-10 w-72 rounded-lg bg-white p-4 text-sm shadow-lg ring-1 ring-black ring-opacity-5 dark:bg-gray-800">
            <p className="font-medium text-gray-900 dark:text-white">
              {info.title}
            </p>
            <p className="mt-2 text-gray-600 dark:text-gray-400">
              {info.description}
            </p>
            {info.tips && (
              <ul className="mt-3 space-y-2">
                {info.tips.map((tip: string, i: number) => (
                  <li key={i} className="flex items-start text-xs text-gray-500 dark:text-gray-400">
                    <RiLightbulbLine className="mr-2 mt-0.5 h-4 w-4 flex-shrink-0 text-indigo-400" />
                    <span>{tip}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        )}
      </div>
    )
  }

  /**
   * Formats a metric value for display with proper formatting
   * 
   * @param {number} value - The metric value to format
   * @param {boolean} isCurrency - Whether to format as currency
   * @param {number} [maxValue] - Maximum value to cap at (for security and UI)
   * @returns {string} The formatted value
   */
  const formatMetric = (value: number, isCurrency = false, maxValue = 1000000000): string => {
    // Handle edge cases: NaN, Infinity, -Infinity
    if (isNaN(value)) return 'N/A'
    if (!isFinite(value)) return value > 0 ? '∞' : '-∞'
    
    // Cap extremely large values for security and UI purposes
    if (Math.abs(value) > maxValue) {
      value = value > 0 ? maxValue : -maxValue
    }
    
    return formatInputValue(value, {
      isCurrency,
      minimumFractionDigits: isCurrency ? 2 : 1,
      maximumFractionDigits: 2
    })
  }

  /**
   * Formats text containing dollar amounts and metrics using proper number formatting
   * 
   * @param {string} text - The text containing dollar values to format
   * @returns {string} Formatted text with properly formatted numbers
   */
  const formatRecommendationText = (text: string): string => {
    // Replace dollar amounts like $X.XX with properly formatted currency
    return text.replace(/\$(\d+(\.\d+)?)/g, (_, amount: string) => {
      return formatMetric(parseFloat(amount), true)
    })
  }

  return (
    <div className="mx-auto max-w-4xl" role="main" aria-labelledby="calculator-heading">
      <div className="rounded-2xl bg-white shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
        {/* Calculator Header */}
        <div className="px-8 pt-8">
          <h2 id="calculator-heading" className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white">
            Marketing Efficiency Ratio Calculator
          </h2>
          <p className="mt-2 text-lg text-gray-600 dark:text-gray-400">
            Calculate your MER, aMER, and nMER to evaluate your marketing performance
          </p>
        </div>

        {/* Calculator Form */}
        <div className="mt-8 grid gap-8 px-8 pb-8 sm:grid-cols-2">
          {/* Left Column - Basic Inputs */}
          <div className="space-y-6">
            <div>
              <div className="flex items-center">
                <label htmlFor="total-revenue" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Total Revenue ($)
                </label>
                {renderTooltip('totalRevenue')}
              </div>
              <NumberInput
                id="total-revenue"
                value={inputs.totalRevenue}
                onChange={(value) => { setInputs({ ...inputs, totalRevenue: value }); trackInteract('input_change') }}
                label=""
                isCurrency={true}
                showCents={false}
                min={0}
                placeholder="Enter total revenue"
                description="Total revenue from all sources during the period"
              />
            </div>

            <div>
              <div className="flex items-center">
                <label htmlFor="marketing-spend" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Total Marketing Spend ($)
                </label>
                {renderTooltip('totalMarketingSpend')}
              </div>
              <NumberInput
                id="marketing-spend"
                value={inputs.totalMarketingSpend}
                onChange={(value) => { setInputs({ ...inputs, totalMarketingSpend: value }); trackInteract('input_change') }}
                label=""
                isCurrency={true}
                min={1}
                showCents={false}
                placeholder="Enter marketing spend"
                description="All marketing expenses including ads, creative, and team costs"
              />
            </div>
          </div>

          {/* Right Column - Advanced Inputs */}
          <div className="space-y-6">
            <div>
              <div className="flex items-center">
                <label htmlFor="attributed-revenue" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  Attributed Revenue ($)
                </label>
                {renderTooltip('attributedRevenue')}
              </div>
              <NumberInput
                id="attributed-revenue"
                value={inputs.attributedRevenue}
                onChange={(value) => { setInputs({ ...inputs, attributedRevenue: value }); trackInteract('input_change') }}
                label=""
                isCurrency={true}
                showCents={false}
                min={0}
                placeholder="Enter attributed revenue"
                description="Revenue directly attributed to marketing channels"
              />
            </div>

            <div>
              <div className="flex items-center">
                <label htmlFor="new-customer-revenue" className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                  New Customer Revenue ($)
                </label>
                {renderTooltip('newCustomerRevenue')}
              </div>
              <NumberInput
                id="new-customer-revenue"
                value={inputs.newCustomerRevenue}
                onChange={(value) => { setInputs({ ...inputs, newCustomerRevenue: value }); trackInteract('input_change') }}
                label=""
                isCurrency={true}
                min={0}
                showCents={false}
                placeholder="Enter new customer revenue"
                description="Revenue from first-time customers only"
              />
            </div>
          </div>
        </div>

        {/* Validation Warnings */}
        {validationMessages.length > 0 && (
          <div className="px-8 py-2 pb-6">
            <div className="rounded-md bg-yellow-50 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800 p-4">
              <div className="flex">
                <div className="flex-shrink-0">
                  <RiAlertLine className="h-5 w-5 text-yellow-400" aria-hidden="true" />
                </div>
                <div className="ml-3">
                  <h3 className="text-sm font-medium text-yellow-800 dark:text-yellow-300">
                    Input Validation Warnings
                  </h3>
                  <div className="mt-2 text-sm text-yellow-700 dark:text-yellow-400">
                    <ul className="list-disc pl-5 space-y-1">
                      {validationMessages.map((warning, index) => (
                        <li key={index}>{warning.message}</li>
                      ))}
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Calculate Button */}
        <div className="border-t border-gray-200 bg-gray-50 px-8 py-6 dark:border-gray-800 dark:bg-gray-900/50 rounded-b-2xl">
          <button
            onClick={handleCalculate}
            className="w-full rounded-lg bg-indigo-600 px-4 py-3 text-center text-sm font-semibold text-white shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            aria-label="Calculate marketing efficiency metrics"
          >
            Calculate Marketing Efficiency
          </button>
        </div>
      </div>

      {/* Results Section */}
      <AnimatePresence>
        {results && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -20 }}
            className="mt-8 space-y-8 rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800"
            role="region"
            aria-label="Calculation Results"
          >
            {/* Primary Metrics */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Marketing Efficiency Metrics
              </h3>
              
              {/* Display warning if calculation was done with validation warnings */}
              {validationMessages.length > 0 && (
                <div className="mt-2 rounded-md bg-yellow-50 p-2 dark:bg-yellow-900/20 border border-yellow-200 dark:border-yellow-800">
                  <p className="text-xs text-yellow-700 dark:text-yellow-400">
                    <RiAlertLine className="inline-block h-3 w-3 mr-1" />
                    Note: These calculations have {validationMessages.length} validation {validationMessages.length === 1 ? 'warning' : 'warnings'}.
                  </p>
                </div>
              )}

              <dl className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-3">
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                  <dt className="flex items-center text-sm font-medium text-gray-500 dark:text-gray-400">
                    <span>MER</span>
                    {renderTooltip('mer')}
                    <Link href="/resources/glossary/metrics/marketing-efficiency-ratio-mer" className="ml-auto text-xs text-indigo-600 hover:text-indigo-500 dark:text-indigo-400">
                      Learn more
                    </Link>
                  </dt>
                  <dd className="mt-2 text-2xl font-semibold text-indigo-600 dark:text-indigo-400 truncate">
                    {isNaN(results.mer) ? 'N/A' : 
                     !isFinite(results.mer) ? '∞' : 
                     `${results.mer.toFixed(2)}x`}
                  </dd>
                  <p className="mt-1 text-xs text-gray-500">
                    {formatMetric(inputs.totalRevenue, true)} ÷ {formatMetric(inputs.totalMarketingSpend, true)}
                  </p>
                </div>
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                  <dt className="flex items-center text-sm font-medium text-gray-500 dark:text-gray-400">
                    <span>aMER</span>
                    {renderTooltip('amer')}
                    <Link href="/resources/glossary/metrics/attributed-marketing-efficiency-ratio-amer" className="ml-auto text-xs text-indigo-600 hover:text-indigo-500 dark:text-indigo-400">
                      Learn more
                    </Link>
                  </dt>
                  <dd className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white truncate">
                    {isNaN(results.amer) ? 'N/A' : 
                     !isFinite(results.amer) ? '∞' : 
                     `${results.amer.toFixed(2)}x`}
                  </dd>
                  <p className="mt-1 text-xs text-gray-500">
                    {formatMetric(inputs.attributedRevenue, true)} ÷ {formatMetric(inputs.totalMarketingSpend, true)}
                  </p>
                </div>
                <div className="rounded-lg bg-gray-50 p-4 dark:bg-gray-800">
                  <dt className="flex items-center text-sm font-medium text-gray-500 dark:text-gray-400">
                    <span>nMER</span>
                    {renderTooltip('nmer')}
                    <Link href="/resources/glossary/metrics/new-marketing-efficiency-ratio-nmer" className="ml-auto text-xs text-indigo-600 hover:text-indigo-500 dark:text-indigo-400">
                      Learn more
                    </Link>
                  </dt>
                  <dd className="mt-2 text-2xl font-semibold text-gray-900 dark:text-white truncate">
                    {isNaN(results.nmer) ? 'N/A' : 
                     !isFinite(results.nmer) ? '∞' : 
                     `${results.nmer.toFixed(2)}x`}
                  </dd>
                  <p className="mt-1 text-xs text-gray-500">
                    {formatMetric(inputs.newCustomerRevenue, true)} ÷ {formatMetric(inputs.totalMarketingSpend, true)}
                  </p>
                </div>
              </dl>
            </div>
            
            {/* Insights & Recommendations */}
            <div>
              <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                Insights & Recommendations
              </h3>
              
              {/* Performance Summary Card */}
              <div className="mt-4 rounded-lg bg-indigo-50 p-5 dark:bg-indigo-900/20">
                <h4 className="font-medium text-indigo-800 dark:text-indigo-300">Performance Summary</h4>
                <p className="mt-2 text-sm text-indigo-700 dark:text-indigo-300">
                  Your overall marketing efficiency (MER) is <span className="font-semibold">
                    {isNaN(results.mer) ? 'N/A' : 
                     !isFinite(results.mer) ? '∞' : 
                     `${results.mer.toFixed(2)}x`}
                  </span>, 
                  generating <span className="font-semibold">
                    {isNaN(results.mer) ? 'N/A' : 
                     !isFinite(results.mer) ? '∞' : 
                     formatMetric(results.mer, true)}
                  </span> in revenue for every $1 spent on marketing.
                  {results.mer >= 4 ? 
                    " This is an excellent ratio that exceeds industry benchmarks." : 
                    results.mer >= 3 ? 
                    " This is a healthy ratio that meets industry benchmarks." : 
                    results.mer >= 2 ? 
                    " This is an acceptable ratio but has room for improvement." : 
                    isNaN(results.mer) || !isFinite(results.mer) ?
                    " Please ensure your marketing spend is greater than zero for accurate metrics." :
                    " This ratio is below typical benchmarks and needs attention."}
                </p>
                
                {/* Gap Analysis */}
                {!isNaN(results.mer) && isFinite(results.mer) && 
                 !isNaN(results.amer) && isFinite(results.amer) && 
                 Math.abs(results.mer - results.amer) > 0.5 && (
                  <div className="mt-3 flex items-start">
                    <div className="mr-2 mt-0.5 rounded-full bg-indigo-100 p-1 dark:bg-indigo-800">
                      <RiInformationLine className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                    </div>
                    <p className="text-xs text-indigo-700 dark:text-indigo-300">
                      The gap between your MER ({results.mer.toFixed(2)}x) and aMER ({results.amer.toFixed(2)}x) indicates 
                      {results.mer > results.amer ? 
                        " significant organic or indirect revenue contribution. This suggests your brand marketing is effective." : 
                        " unusual attribution patterns. Verify your tracking is correctly configured."}
                    </p>
                  </div>
                )}
              </div>
              
              {/* Key Insights Section */}
              <div className="mt-4 rounded-lg bg-amber-50 p-5 dark:bg-amber-900/20">
                <h4 className="font-medium text-amber-800 dark:text-amber-300">Key Insights</h4>
                
                {/* Acquisition Efficiency */}
                <div className="mt-3 flex items-start">
                  <div className="mr-2 mt-0.5 rounded-full bg-amber-100 p-1 dark:bg-amber-800">
                    <RiUserAddLine className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                      Customer Acquisition Efficiency
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      Your nMER is {
                        isNaN(results.nmer) ? 'N/A' : 
                        !isFinite(results.nmer) ? '∞' : 
                        `${formatMetric(results.nmer, false)}x`
                      }, meaning you generate {
                        isNaN(results.nmer) ? 'N/A' : 
                        !isFinite(results.nmer) ? '∞' : 
                        formatMetric(results.nmer, true)
                      } in new customer revenue for each $1 spent.
                      {isNaN(results.nmer) || !isFinite(results.nmer) ?
                        " Please ensure your marketing spend is greater than zero for accurate metrics." :
                        results.nmer < 1 ? 
                        " This indicates you're spending more to acquire customers than they spend on their first purchase." : 
                        results.nmer < 2 ? 
                        " This is sustainable if your customer lifetime value is strong." : 
                        " This is a healthy acquisition ratio."}
                    </p>
                  </div>
                </div>
                
                {/* Attribution Analysis */}
                <div className="mt-3 flex items-start">
                  <div className="mr-2 mt-0.5 rounded-full bg-amber-100 p-1 dark:bg-amber-800">
                    <RiPercentLine className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                      Attribution Analysis
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      {isNaN(inputs.attributedRevenue / inputs.totalRevenue) || !isFinite(inputs.attributedRevenue / inputs.totalRevenue) ? 
                        "Attribution rate cannot be calculated. Please check your revenue inputs." :
                        `${formatMetric(inputs.attributedRevenue / inputs.totalRevenue * 100, false)}% of your revenue is directly attributed to marketing channels.`
                      }
                      {isNaN(inputs.attributedRevenue / inputs.totalRevenue) || !isFinite(inputs.attributedRevenue / inputs.totalRevenue) ? 
                        "" :
                        inputs.attributedRevenue / inputs.totalRevenue < 0.4 ? 
                        " This low attribution rate suggests either strong organic growth or potential tracking issues." : 
                        inputs.attributedRevenue / inputs.totalRevenue > 0.8 ? 
                        " This high attribution rate indicates heavy reliance on paid channels." : 
                        " This is a balanced attribution mix."
                      }
                    </p>
                  </div>
                </div>
                
                {/* Returning Customer Value */}
                <div className="mt-3 flex items-start">
                  <div className="mr-2 mt-0.5 rounded-full bg-amber-100 p-1 dark:bg-amber-800">
                    <RiMoneyDollarCircleLine className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="text-sm font-medium text-amber-700 dark:text-amber-300">
                      Returning Customer Value
                    </p>
                    <p className="text-xs text-amber-600 dark:text-amber-400">
                      {((inputs.totalRevenue - inputs.newCustomerRevenue) / inputs.totalRevenue * 100).toFixed(0)}% of your revenue comes from returning customers.
                      {(inputs.totalRevenue - inputs.newCustomerRevenue) / inputs.totalRevenue < 0.3 ? 
                        " Your business is heavily dependent on new customer acquisition." : 
                        (inputs.totalRevenue - inputs.newCustomerRevenue) / inputs.totalRevenue > 0.7 ? 
                        " Your business has strong customer retention and repeat purchase behavior." : 
                        " You have a healthy balance between new and returning customer revenue."}
                    </p>
                  </div>
                </div>
              </div>
              
              {/* Recommendations */}
              <div className="mt-8 space-y-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">Recommendations</h3>
                
                <div className="mt-4 space-y-3">
                  {results.recommendations.map((rec, index) => (
                    <div
                      key={index}
                      className={cn(
                        "rounded-lg p-4",
                        rec.type === 'primary' ? "bg-indigo-50 dark:bg-indigo-900/20" : 
                        rec.type === 'optimization' ? "bg-amber-50 dark:bg-amber-900/20" : 
                        "bg-gray-50 dark:bg-gray-800"
                      )}
                    >
                      <div className="flex items-center">
                        <div className="flex-shrink-0">
                          <span className={cn(
                            "flex h-8 w-8 items-center justify-center rounded-full",
                            rec.type === 'primary' ? "bg-indigo-100 dark:bg-indigo-800" :
                            rec.type === 'optimization' ? "bg-amber-100 dark:bg-amber-800" :
                            "bg-gray-100 dark:bg-gray-700"
                          )}>
                            {rec.icon === 'chart' && <RiLineChartLine className="h-5 w-5 text-indigo-600 dark:text-indigo-400" />}
                            {rec.icon === 'attribution' && <RiPercentLine className="h-5 w-5 text-amber-600 dark:text-amber-400" />}
                            {rec.icon === 'acquisition' && <RiUserAddLine className="h-5 w-5 text-amber-600 dark:text-amber-400" />}
                            {rec.icon === 'strategy' && <RiMoneyDollarCircleLine className="h-5 w-5 text-gray-600 dark:text-gray-400" />}
                            {rec.icon === 'warning' && <RiAlertLine className="h-5 w-5 text-amber-600 dark:text-amber-400" />}
                            {!rec.icon && <RiLightbulbLine className="h-5 w-5 text-gray-600 dark:text-gray-400" />}
                          </span>
                        </div>
                        <p className={cn(
                          "ml-4 text-sm",
                          rec.type === 'primary' ? "text-indigo-700 dark:text-indigo-300" :
                          rec.type === 'optimization' ? "text-amber-700 dark:text-amber-300" :
                          "text-gray-700 dark:text-gray-300"
                        )}>
                          {formatRecommendationText(rec.text)}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Email Capture */}
            {showEmailCapture && results && (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="rounded-lg bg-indigo-50 dark:bg-indigo-900/20"
              >
                <EmbeddedEmailCapture {...emailCaptureConfig} />
              </motion.div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* FAQ Section */}
      <div className="mt-12 rounded-2xl bg-white p-8 shadow-xl ring-1 ring-gray-200 dark:bg-gray-900 dark:ring-gray-800">
        <h3 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">
          Frequently Asked Questions
        </h3>
        <FAQ 
          items={FAQ_ITEMS} 
          type="single"
          disableSchema={true}
          features={{ showTitle: false }}
          theme={{ size: "lg" }}
        />
      </div>
    </div>
  )
}

export default MerCalculator
